import { Badge, Button, Spinner, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Stocktake, StocktakeLine } from '@warehouse/shared';
import { ArrowLeft, Check, ClipboardCheck } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { warehouseApi, warehouseKeys, type StocktakeBinProgress } from '../api';
import { QtyKeypad, submitValue } from '../components/QtyKeypad';
import { formatQty } from '../../../lib/format';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * The tablet counting UI (`/stocktaking`, ARCHITECTURE §9).
 *
 * Full-bleed, three screens deep, nothing smaller than a thumb:
 *   pick a stocktake → pick a bin → count its lines one at a time.
 *
 * Every count is `POST /warehouse/stocktakes/count`, which dispatches the
 * `stocktake.count` command — the same handler the Android app will drain from
 * its offline outbox. A stale expected quantity comes back as `healed` and is
 * shown to the counter rather than silently overwritten.
 */
export function CountingPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [binId, setBinId] = useState<string | null>(null);

  if (!id) return <StocktakePicker onPick={(picked) => void navigate(`/stocktaking/${picked}`)} />;

  return binId ? (
    <BinCounter
      stocktakeId={id}
      binId={binId}
      onBack={() => {
        setBinId(null);
      }}
    />
  ) : (
    <BinPicker
      stocktakeId={id}
      onPick={setBinId}
      onExit={() => {
        void navigate('/warehouse/stocktaking');
      }}
    />
  );
}

// ---------------------------------------------------------------------------

function Shell({ children }: { children: ReactNode }): ReactNode {
  return <div className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-4 p-4">{children}</div>;
}

function StocktakePicker({ onPick }: { onPick: (id: string) => void }): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { data, isPending } = useQuery({
    queryKey: warehouseKeys.stocktakes({ counting: true }),
    queryFn: () =>
      warehouseApi.listStocktakes({
        page: 1,
        pageSize: 50,
        filters: { status: ['counting', 'review'] },
        sort: 'createdAt',
        dir: 'desc',
      }),
  });
  useLiveQuery(['stocktakes'], warehouseKeys.stocktakes({ counting: true }));

  const active: Stocktake[] = data?.items ?? [];

  return (
    <Shell>
      <h1 className="font-display text-2xl font-semibold text-ink">
        {t('warehouseCore:count.selectStocktake')}
      </h1>

      {isPending ? (
        <Spinner />
      ) : active.length === 0 ? (
        <Surface variant="subtle" className="p-6">
          <p className="text-base text-ink">{t('warehouseCore:count.noActive')}</p>
          <p className="mt-1 text-sm text-ink-muted">{t('warehouseCore:count.noActiveHelp')}</p>
        </Surface>
      ) : (
        <ul className="flex flex-col gap-3">
          {active.map((take) => (
            <li key={take.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(take.id);
                }}
                className="flex w-full items-center justify-between gap-4 rounded-2xl border border-surface-border bg-surface px-5 py-5 text-left transition-colors active:bg-brand-500/10"
              >
                <span className="min-w-0">
                  <span className="block truncate text-lg font-medium text-ink">{take.name}</span>
                  <span className="text-sm text-ink-muted">
                    {t('warehouseCore:stocktaking.progress', {
                      counted: take.countedCount,
                      total: take.lineCount,
                    })}
                  </span>
                </span>
                <Badge tone="brand">{t(`warehouseCore:stocktaking.statuses.${take.status}`)}</Badge>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Shell>
  );
}

function BinPicker({
  stocktakeId,
  onPick,
  onExit,
}: {
  stocktakeId: string;
  onPick: (binId: string) => void;
  onExit: () => void;
}): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const [scan, setScan] = useState('');

  const stocktake = useQuery({
    queryKey: warehouseKeys.stocktake(stocktakeId),
    queryFn: () => warehouseApi.getStocktake(stocktakeId),
  });
  const bins = useQuery({
    queryKey: warehouseKeys.stocktakeBins(stocktakeId),
    queryFn: () => warehouseApi.listStocktakeBins(stocktakeId),
  });
  useLiveQuery(['stocktakes'], warehouseKeys.stocktakeBins(stocktakeId));

  const rows: StocktakeBinProgress[] = bins.data ?? [];
  const filtered = useMemo(() => {
    const term = scan.trim().toUpperCase();
    return term ? rows.filter((r) => r.binCode.includes(term)) : rows;
  }, [rows, scan]);

  return (
    <Shell>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" icon={<ArrowLeft />} onClick={onExit}>
          {t('common:actions.back')}
        </Button>
        <h1 className="min-w-0 flex-1 truncate font-display text-xl font-semibold text-ink">
          {stocktake.data?.name ?? t('warehouseCore:count.title')}
        </h1>
      </div>

      <input
        type="search"
        inputMode="search"
        autoFocus
        value={scan}
        onChange={(event) => {
          setScan(event.target.value.toUpperCase());
        }}
        placeholder={t('warehouseCore:count.scanBin')}
        aria-label={t('warehouseCore:count.scanBin')}
        className="w-full rounded-2xl border border-surface-border bg-surface px-5 py-4 font-mono text-xl text-ink"
      />

      {bins.isPending ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <Surface variant="subtle" className="p-6 text-base text-ink-muted">
          {t('warehouseCore:stocktaking.noLines')}
        </Surface>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {filtered.map((bin) => {
            const done = bin.countedCount >= bin.lineCount;
            return (
              <li key={bin.binId}>
                <button
                  type="button"
                  data-testid={`count-bin-${bin.binCode}`}
                  onClick={() => {
                    onPick(bin.binId);
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-5 py-5 text-left transition-colors active:bg-brand-500/10 ${
                    done ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-surface-border bg-surface'
                  }`}
                >
                  <span className="font-mono text-xl text-ink">{bin.binCode}</span>
                  {done ? (
                    <Badge tone="success">{t('warehouseCore:count.binDone')}</Badge>
                  ) : (
                    <span className="font-mono text-base text-ink-muted">
                      {t('warehouseCore:count.binProgress', {
                        counted: bin.countedCount,
                        total: bin.lineCount,
                      })}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Shell>
  );
}

function BinCounter({
  stocktakeId,
  binId,
  onBack,
}: {
  stocktakeId: string;
  binId: string;
  onBack: () => void;
}): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [buffer, setBuffer] = useState('');

  const lines = useQuery({
    queryKey: warehouseKeys.stocktakeLines(stocktakeId, { binId }),
    queryFn: () =>
      warehouseApi.listStocktakeLines(stocktakeId, {
        page: 1,
        pageSize: 200,
        filters: { binId },
        sort: 'articleNo',
        dir: 'asc',
      }),
  });

  const items: StocktakeLine[] = useMemo(() => lines.data?.items ?? [], [lines.data]);
  const line = items[index];

  // Moving to another line must not carry the previous count over — a counter
  // tapping "next" and then "save" would otherwise post the wrong quantity.
  useEffect(() => {
    setBuffer(line?.countedQtyBase ?? '');
  }, [line?.id, line?.countedQtyBase]);

  const save = useMutation({
    mutationFn: () =>
      warehouseApi.count({
        stocktakeId,
        lineId: line!.id,
        countedQtyBase: submitValue(buffer),
        observedExpectedQtyBase: line!.expectedQtyBase,
      }),
    onSuccess: (result) => {
      if (result.verdict === 'healed') {
        toastInfo(
          t('warehouseCore:count.healed', {
            expected: formatQty(String(result.healing?.['expectedQtyBase'] ?? '')),
          }),
        );
      } else {
        toastSuccess(t('warehouseCore:count.saved'));
      }
      void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      setIndex((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
    },
    onError: (error) => {
      toastError(error);
    },
  });

  if (lines.isPending) {
    return (
      <Shell>
        <Spinner />
      </Shell>
    );
  }

  if (!line) {
    return (
      <Shell>
        <Button variant="ghost" size="sm" icon={<ArrowLeft />} onClick={onBack}>
          {t('warehouseCore:count.back')}
        </Button>
        <Surface variant="subtle" className="p-6 text-base text-ink-muted">
          {t('warehouseCore:count.allCounted')}
        </Surface>
      </Shell>
    );
  }

  const expected = Number(line.expectedQtyBase);
  const counted = Number(submitValue(buffer));
  const diff = counted - expected;

  return (
    <Shell>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" icon={<ArrowLeft />} onClick={onBack}>
          {t('warehouseCore:count.back')}
        </Button>
        <span className="font-mono text-lg text-ink">{line.binCode}</span>
        <span className="ml-auto text-sm text-ink-muted">
          {t('warehouseCore:count.lineOf', { current: index + 1, total: items.length })}
        </span>
      </div>

      <Surface className="flex flex-col gap-1 p-5">
        <p className="text-xl font-medium text-ink">{line.articleName}</p>
        <p className="font-mono text-sm text-ink-muted">{line.articleNo}</p>
        {line.lotNo ? (
          <p className="text-sm text-ink-muted">
            {t('warehouseCore:stocktaking.lot')} {line.lotNo}
            {line.bestBefore ? ` · ${line.bestBefore}` : ''}
          </p>
        ) : null}
        {line.lpn ? <p className="font-mono text-sm text-ink-muted">{line.lpn}</p> : null}
        <p className="mt-2 text-sm text-ink-muted">
          {t('warehouseCore:count.expected')}:{' '}
          <span className="font-mono text-ink">
            {formatQty(line.expectedQtyBase, { unit: line.unitCode ?? undefined })}
          </span>
        </p>
      </Surface>

      <QtyKeypad value={buffer} onChange={setBuffer} disabled={save.isPending} />

      {buffer.length > 0 ? (
        <p
          data-testid="count-diff"
          className={`text-center text-base ${diff === 0 ? 'text-ink-muted' : 'text-amber-600'}`}
        >
          {diff === 0
            ? t('warehouseCore:count.match')
            : t('warehouseCore:count.diffWarning', { diff: formatQty(diff.toFixed(3)) })}
        </p>
      ) : null}

      <div className="mt-auto flex flex-col gap-2 pb-2">
        <Button
          variant="secondary"
          className="h-14 text-base"
          onClick={() => {
            setBuffer(line.expectedQtyBase);
          }}
        >
          {t('warehouseCore:count.sameAsExpected')}
        </Button>
        <Button
          variant="primary"
          className="h-16 text-lg"
          icon={<Check />}
          loading={save.isPending}
          disabled={buffer.length === 0}
          onClick={() => {
            save.mutate();
          }}
        >
          {t('warehouseCore:count.save')}
        </Button>
        {index < items.length - 1 ? (
          <Button
            variant="ghost"
            className="h-12"
            icon={<ClipboardCheck />}
            onClick={() => {
              setIndex((i) => i + 1);
            }}
          >
            {t('warehouseCore:count.nextLine')}
          </Button>
        ) : null}
      </div>
    </Shell>
  );
}
